/**
 * MapAnything PLY Inference Plugin
 *
 * Video -> semantic point cloud (.ply) via MapAnything.
 */

import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import { INFERENCE_API_URL, getVideoBlob, createObjectFromInference, inferencePost, POINTCLOUD_SCALE_CORRECTION, type InferenceStatusCallback } from './shared';
import { declareInferencePlugin } from '../plugins/inference-adapter';

async function run(sourceObject: SceneObject, traceScene: TraceScene, onStatus?: InferenceStatusCallback): Promise<SceneObject> {
    const videoBlob = await getVideoBlob(sourceObject, traceScene);
    const vidExt = sourceObject.src.split('.').pop()?.toLowerCase() ?? 'mp4';

    const response = await inferencePost('mapanything', () => {
        const formData = new FormData();
        formData.append('video', videoBlob, `video.${vidExt}`);
        return [`${INFERENCE_API_URL}/mapanything/predict-video`, { method: 'POST', body: formData }];
    }, undefined, onStatus);
    if (!response.ok) {
        const errText = await response.text();
        throw new Error(`MapAnything API error (${response.status}): ${errText}`);
    }

    const plyBlob = await response.blob();
    const assetPath = await traceScene.resources.addAssetWithDedup(
        `assets/${sourceObject.name}_map.ply`,
        plyBlob,
    );

    return createObjectFromInference(
        traceScene, sourceObject, assetPath, 'map', POINTCLOUD_SCALE_CORRECTION,
    );
}

declareInferencePlugin({
    id: 'mapanything-ply',
    sourceType: 'video',
    menuLabel: 'Make Map PLY (MapAnything)',
    menuOrder: 202,

    toolName: 'generate_map',

    run,

    slashCommand: {
        name: 'map',
        description: 'Convert video to semantic point cloud',
        usage: '[object]',
        category: 'inference',
        parseArgs: (args, selected) => ({
            object: args || selected || 'selected',
        }),
    },
});
